'use client';
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { IconCheckboxFilled } from '@repo/svgs';

export interface ToastProps {
  /** Message to display */
  message: string;
  /** Whether the toast is visible */
  visible: boolean;
  /** Duration in milliseconds before auto-hiding (0 = no auto-hide) */
  duration?: number;
  /** Callback when toast is dismissed */
  onDismiss?: () => void;
  /** Optional icon to show */
  icon?: React.ReactNode;
  className?: string;
}

/**
 * Toast component for displaying temporary success/error messages
 */
export function Toast({
  message,
  visible,
  duration = 3000,
  onDismiss,
  icon,
  className,
}: ToastProps) {
  const [shouldRender, setShouldRender] = useState(visible);

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      if (duration > 0) {
        const timer = setTimeout(() => {
          setShouldRender(false);
          onDismiss?.();
        }, duration);
        return () => clearTimeout(timer);
      }
    } else {
      // Delay unmounting to allow fade-out animation
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [visible, duration, onDismiss]);

  if (!shouldRender) return null;

  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-2',
        'px-4 py-3 rounded-full',
        'bg-secondary border border-secondary',
        'cursor-pointer',
        'shadow-lg',
        'transition-all duration-300 ease-in-out',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
        'text-primary',
        className,
      )}
      onClick={onDismiss}
      role="alert"
      aria-live="polite"
    >
      {icon ?? <IconCheckboxFilled className="h-4 w-4 icon-primary" />}
      <span className="text-body-2">{message}</span>
    </div>
  );
}

export default Toast;
